import React, { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { styles } from '../../../styles/themes/style';
import { ICampaign } from '../../../interfaces/models/campaign';
import { useCampaignsRequest } from '../../../services/apis/campaigns/useCampaignsRequest';
import { AppRoutes } from '../../../interfaces/store/appStore';
import Text from '../../../components/Text';
import CampaignLeadsView from '../components/campaigns/CampaignLeadsView';
import CampaignPanelBoundary from '../components/campaigns/CampaignPanelBoundary';
import { PROXY_UI_POLL_MS, SIM_EVENT } from '../hooks/useCampaignSimulation';

const CampaignLeadsPage: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCampaignById } = useCampaignsRequest();

  const [campaign, setCampaign] = useState<ICampaign | null>(null);
  const [loading, setLoading] = useState(true);

  const autoFetch = !!(location.state as any)?.autoFetch;
  const [highlightStartPanel] = useState(() => !!(location.state as any)?.showStartPanel);

  useEffect(() => {
    if (!id) { navigate(AppRoutes.CAMPAIGNS); return; }
    getCampaignById(id).then(data => {
      if (data?.campaign) {
        const c = autoFetch ? { ...data.campaign, _autoFetch: true } : data.campaign;
        setCampaign(c as ICampaign);
        // Clear nav state so refresh doesn't re-trigger one-shot flags
        if (autoFetch || highlightStartPanel) navigate('.', { replace: true, state: {} });
      } else {
        navigate(AppRoutes.CAMPAIGNS);
      }
      setLoading(false);
    });
  }, [id]);

  // Keep the header controls (Start/Pause/Resume) in sync with backend changes
  // that happen outside this page (e.g. simulation auto-pausing a campaign).
  useEffect(() => {
    if (!id) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handler = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        getCampaignById(id).then(data => {
          if (data?.campaign) setCampaign(prev => prev ? ({ ...prev, ...data.campaign }) as ICampaign : (data.campaign as ICampaign));
        });
      }, 1500);
    };
    window.addEventListener(SIM_EVENT, handler);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(SIM_EVENT, handler);
    };
  }, [id, getCampaignById]);

  // Proxy: cloud executor / Unipile update MongoDB with no SIM_EVENT — poll campaign meta.
  useEffect(() => {
    if (!id || !campaign) return;
    if (campaign.executionMethod !== 'proxy') return;
    if (campaign.status !== 'active' && campaign.status !== 'paused') return;
    const interval = window.setInterval(() => {
      getCampaignById(id).then(data => {
        if (data?.campaign) {
          setCampaign(prev => prev ? ({ ...prev, ...data.campaign }) as ICampaign : (data.campaign as ICampaign));
        }
      });
    }, PROXY_UI_POLL_MS);
    return () => window.clearInterval(interval);
  }, [id, campaign?.executionMethod, campaign?.status, getCampaignById]);

  if (loading) {
    return (
      <LoadingWrap>
        <Text $paragraph2 style={{ color: styles.colors.black300 }}>Loading…</Text>
      </LoadingWrap>
    );
  }

  if (!campaign) return null;

  return (
    <PageWrap>
      <CampaignPanelBoundary>
        <CampaignLeadsView
          campaign={campaign}
          fullPage
          highlightStartPanel={highlightStartPanel}
          onCampaignUpdate={updated => setCampaign(updated)}
          onClose={() => navigate(AppRoutes.CAMPAIGNS)}
        />
      </CampaignPanelBoundary>
    </PageWrap>
  );
};

const PageWrap = styled.div`
  width: 100%; height: 100%; display: flex; flex-direction: column; overflow: hidden;
`;

const LoadingWrap = styled.div`
  display: flex; align-items: center; justify-content: center; height: 100%;
`;

export default CampaignLeadsPage;
